import { REASON_RENDER_RANK } from './constants';
import {
  ImprovedPolicyGenerationEligibility,
  ImprovedPolicyGenerationErrorBody,
  ImprovedPolicyGenerationReason,
} from 'src/types/workflowBuilderAPITypes';

/**
 * Narrow an unknown RTK Query error to the generation endpoints' structured
 * body ({status, message, payload: {error_code, ...}} on `error.data`).
 * Anything else — HTML proxy bodies, network failures, plain-string 5xx —
 * narrows to undefined so callers fall back to generic copy.
 */
export const getImprovedPolicyGenerationError = (
  error: unknown,
): ImprovedPolicyGenerationErrorBody | undefined => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return undefined;
  }
  const data = (error as { data: unknown }).data;
  if (!data || typeof data !== 'object' || !('payload' in data)) {
    return undefined;
  }
  const payload = (data as { payload: unknown }).payload;
  if (
    !payload ||
    typeof payload !== 'object' ||
    typeof (payload as { error_code?: unknown }).error_code !== 'string'
  ) {
    return undefined;
  }
  return data as ImprovedPolicyGenerationErrorBody;
};

/**
 * Runtime guard for the eligibility result embedded in an eligibility_failed
 * error, checked before it is upserted into the eligibility query cache — a
 * malformed payload must degrade to a refetch, not poison the cache.
 */
export const isEligibilityResult = (
  value: unknown,
): value is ImprovedPolicyGenerationEligibility => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  const signals = candidate.signals as Record<string, unknown> | undefined;
  return (
    typeof candidate.intent_workflow_id === 'string' &&
    typeof candidate.is_eligible === 'boolean' &&
    Array.isArray(candidate.reasons) &&
    !!signals &&
    typeof signals === 'object' &&
    typeof signals.usable_for_generation === 'number' &&
    typeof signals.unlock_threshold === 'number'
  );
};

/** Sort comparator implementing REASON_RENDER_RANK; unknown codes last. */
export const compareReasonsForRender = (
  a: ImprovedPolicyGenerationReason,
  b: ImprovedPolicyGenerationReason,
): number => {
  const rank = (reason: ImprovedPolicyGenerationReason) =>
    REASON_RENDER_RANK[reason.code] ?? Number.MAX_SAFE_INTEGER;
  return rank(a) - rank(b);
};
